import { useState } from 'react';
import { Lock, CheckCircle2, Loader2 } from 'lucide-react';
import { aud } from '@/utils/format';

interface HostedPaymentMockProps {
  amountAud: number;
  description: string;
  onConfirm: () => void;
  testid?: string;
}

// Provider-hosted payment handoff. The customer never enters card data in our UI:
// this presents as a redirect to a secure payment provider, so card data never
// touches the platform. Provider-agnostic on purpose - no provider is named. The
// mock stands in for the real redirect round-trip; on "return" it calls onConfirm,
// which drives the unchanged deposit / booking / waiver webhook flow in depositFlow.
export default function HostedPaymentMock({
  amountAud,
  description,
  onConfirm,
  testid,
}: HostedPaymentMockProps) {
  const [state, setState] = useState<'idle' | 'processing' | 'success'>('idle');

  const startPayment = () => {
    setState('processing');
    setTimeout(() => {
      setState('success');
      setTimeout(onConfirm, 700);
    }, 1100);
  };

  return (
    <div className="bg-ink-deep border border-gold-dim/30 rounded-xl p-5 text-center" data-testid={testid}>
      {state === 'success' ? (
        <div className="panel-dark p-4 flex items-center gap-3 text-left" data-testid="payment-success">
          <CheckCircle2 size={20} className="text-mint shrink-0" />
          <div>
            <p className="font-inter text-cream">Payment confirmed</p>
            <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
              Deposit received · booking locked in
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="h-11 w-11 rounded-full bg-ink-dark border border-gold-dim/40 grid place-items-center mx-auto text-gold-bright">
            <Lock size={16} />
          </div>
          <p className="font-cinzel text-3xl text-cream mt-4">{aud(amountAud)}</p>
          <p className="font-serif text-cream-soft text-sm mt-1">{description}</p>
          <p className="font-serif text-cream-soft text-sm mt-3">
            {state === 'processing'
              ? 'Taking you to the secure payment provider…'
              : 'You’ll be taken to our secure payment provider to pay your deposit.'}
          </p>
          <button
            type="button"
            onClick={startPayment}
            disabled={state === 'processing'}
            data-testid="pay-securely"
            className="mt-5 w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {state === 'processing' ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Processing
              </>
            ) : (
              <>
                <Lock size={14} /> Pay deposit securely
              </>
            )}
          </button>
          <p className="font-inter text-[10px] text-cream-soft mt-3 uppercase tracking-widest">
            {state === 'processing'
              ? 'Please don’t close this window'
              : 'Your card details never reach Tattoo Vault'}
          </p>
        </>
      )}
    </div>
  );
}
